import { useMutation } from "@tanstack/react-query";
import axios from "axios";

import {
  authScopes,
  LOCAL_STORAGE_EXTERNAL_APP_KEY,
  LOCAL_STORAGE_EXTERNAL_BASE_URL_KEY,
  LOCAL_STORAGE_EXTERNAL_SCOPES_KEY,
} from "@/contexts/auth/utils";
import { appSchema } from "@/schemas";
import toast from "@/toast";
import { parseBaseURL } from "@/utils/auth";
import sourceCode from "@/utils/code";

interface Params {
  host: string;
}

const params = {
  client_name: sourceCode.displayName,
  redirect_uris: `${window.location.origin}/login/external`,
  website: sourceCode.homepage,
  scopes: authScopes,
};

function useAuthorizeExternalSession() {
  return useMutation({
    mutationFn: async ({ host }: Params) => {
      const baseURL = parseBaseURL(`https://${host}`);
      const { data } = await axios.post(`${baseURL}/api/v1/apps`, params);
      return appSchema.parse(data);
    },
    onSuccess(app, variables) {
      const baseURL = parseBaseURL(`https://${variables.host}`);
      const { client_id, redirect_uri } = app as Record<string, string>;

      const query = new URLSearchParams({
        client_id,
        redirect_uri,
        response_type: "code",
        scope: authScopes,
      });

      localStorage.setItem(LOCAL_STORAGE_EXTERNAL_APP_KEY, JSON.stringify(app));
      localStorage.setItem(LOCAL_STORAGE_EXTERNAL_BASE_URL_KEY, baseURL);
      localStorage.setItem(LOCAL_STORAGE_EXTERNAL_SCOPES_KEY, authScopes);

      window.location.href = `${baseURL}/oauth/authorize?${query.toString()}`;
    },
    onError() {
      toast.error("Failed to connect to the external instance.");
    },
  });
}

export { useAuthorizeExternalSession };
